/**
 * 超时处理工具函数
 */

/**
 * 为 Promise 添加超时保护
 * @param promise 原始 Promise
 * @param timeout 超时时间（毫秒），默认 5 分钟
 * @param errorMessage 超时错误消息
 */
export function withTimeout<T>(
    promise: Promise<T>,
    timeout: number = 300000, // 默认 5 分钟超时
    errorMessage = 'Operation timed out'
): Promise<T> {
    const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
            reject(new Error(`${errorMessage} after ${timeout}ms`));
        }, timeout);
    });

    return Promise.race([promise, timeoutPromise]);
}

/**
 * 重试函数，支持指数退避
 * @param fn 要重试的函数
 * @param maxRetries 最大重试次数
 * @param baseDelay 基础延迟时间（毫秒）
 * @param maxDelay 最大延迟时间（毫秒）
 */
export async function retryWithBackoff<T>(
    fn: () => Promise<T>,
    maxRetries = 3,
    baseDelay = 1000,
    maxDelay = 10000
): Promise<T> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            return await fn();
        } catch (error) {
            lastError = error as Error;

            if (attempt === maxRetries) {
                break;
            }

            const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
            console.debug(`Retry attempt ${attempt + 1}/${maxRetries + 1} failed, waiting ${delay}ms...`);
            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }

    throw lastError || new Error('All retry attempts failed');
}

/**
 * 检查错误是否为超时错误
 * @param error 错误对象
 */
export function isTimeoutError(error: any): boolean {
    if (!error) return false;

    const message = error.message || error.toString();
    return (
        message.includes('timeout') ||
        message.includes('TIMEOUT') ||
        message.includes('62000') ||
        error.code === 'TIMEOUT' ||
        error.name === 'TimeoutError'
    );
}

/**
 * 格式化超时错误消息
 * @param error 原始错误
 * @param context 上下文信息
 */
export function formatTimeoutError(error: any, context?: string): string {
    const baseMessage = context ? `${context}: ` : '';

    if (isTimeoutError(error)) {
        return `${baseMessage}请求超时，请稍后重试。如果问题持续存在，请联系技术支持。`;
    }

    return `${baseMessage}${error.message || '未知错误'}`;
}